import { Position, useReactFlow } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FlowHandle } from './commons/flowHandle';
import { useTrashNode } from './commons/trashNode';
import './style.scss';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { UilTrashAlt } from '@iconscout/react-unicons';
import { handleClipboardKeyDown } from '../helper';
import { selectWorkflowType } from 'store/slices/chatbot/chatbotSlice';
import { useWorkflowTypeAdapter } from '../../adapters/workflowTypeAdapter';

/** Matches ApexChat LeadType: None=0, Sales=1, Service=2, Other=3 */
export const END_NODE_LEAD_TYPE = {
  None: 0,
  Sales: 1,
  Service: 2,
  Other: 3
};

const CONVERSATION_TYPE_OPTIONS = [
  { value: END_NODE_LEAD_TYPE.None, label: 'None' },
  { value: END_NODE_LEAD_TYPE.Sales, label: 'Sales' },
  { value: END_NODE_LEAD_TYPE.Service, label: 'Service' },
  { value: END_NODE_LEAD_TYPE.Other, label: 'Other' }
];

function normalizeLeadType(value) {
  const n = Number(value);
  if (
    n === END_NODE_LEAD_TYPE.Sales ||
    n === END_NODE_LEAD_TYPE.Service ||
    n === END_NODE_LEAD_TYPE.Other
  ) {
    return n;
  }
  return END_NODE_LEAD_TYPE.None;
}

export default function EndNode({ id, data, editable = true }) {
  const { setNodes } = useReactFlow();
  const trashNode = useTrashNode();
  const workflowBuildMode = useSelector(selectWorkflowType);
  const { showEndingStatement, showConversationType } = useWorkflowTypeAdapter(
    workflowBuildMode
  ).getEndNodeConfig();

  const [text, setText] = useState(data?.text || '');
  const [endingStatement, setEndingStatement] = useState(
    data?.endingStatement || ''
  );
  const [leadType, setLeadType] = useState(() =>
    normalizeLeadType(data?.leadType)
  );

  // Keep selection in sync when workflow is loaded/replaced from the server
  useEffect(() => {
    setLeadType(normalizeLeadType(data?.leadType));
  }, [data?.leadType]);

  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  const selectLeadType = value => {
    if (!editable) return;
    const next = normalizeLeadType(value);
    setLeadType(next);
    onChange({ leadType: next });
  };

  return (
    <dl className="mt-3" style={{ width: '290px' }}>
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div className="d-flex align-items-center px-4 end-node-header">
          <div className="d-flex mr-auto ml-2">
            <dt className="text-truncate fw-semibold fs-6">End</dt>
          </div>
          <div className="ms-auto">
            <button
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 delete-icon"
              title="Delete end node"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
            </button>
          </div>
        </div>

        <div className="card-body p-2">
          {showEndingStatement && (
            <div className="mb-3 position-relative">
              <label
                className="form-label fw-semibold mb-1"
                style={{ fontSize: '12px' }}
                htmlFor={`ending-${id}`}
              >
                Ending statement
              </label>
              <SimpleLabelTextBox
                dataProperty={endingStatement}
                maxRows={6}
                minRows={2}
                multiline
                editable={editable}
                id={`ending-${id}`}
                handleChange={e => {
                  setEndingStatement(e.target.value);
                  onChange({ endingStatement: e.target.value });
                }}
                onkeyPress={handleClipboardKeyDown}
                inputClasses="custom-scroll"
                placeholder='e.g. "Is there anything else I can help you with?"'
                inputStyles={{ backgroundColor: 'white', padding: '10px' }}
                inputPropClasses="custom-placeholder"
              />
              <div
                className="text-muted mt-1"
                style={{ fontSize: '11px', lineHeight: 1.3 }}
              >
                Optional. Pitched when chat reaches this node; used for the
                anything-else follow-up loop.
              </div>
            </div>
          )}

          <div className="mb-3 position-relative">
            <label
              className="form-label fw-semibold mb-1"
              style={{ fontSize: '12px' }}
              htmlFor={`text-${id}`}
            >
              Closing statement
            </label>
            <SimpleLabelTextBox
              dataProperty={text}
              maxRows={10}
              minRows={2}
              multiline
              editable={editable}
              id={`text-${id}`}
              handleChange={e => {
                setText(e.target.value);
                onChange({ text: e.target.value });
              }}
              onkeyPress={handleClipboardKeyDown}
              inputClasses="custom-scroll"
              placeholder="Write closing message (final goodbye)"
              inputStyles={{ backgroundColor: 'white', padding: '10px' }}
              inputPropClasses="custom-placeholder"
            />
            <div
              className="text-muted mt-1"
              style={{ fontSize: '11px', lineHeight: 1.3 }}
            >
              Final thank-you when the visitor is done (or after the follow-up
              limit).
            </div>
          </div>

          {showConversationType && (
            <div className="mb-1">
              <div
                className="form-label fw-semibold mb-2"
                style={{ fontSize: '12px' }}
              >
                Conversation type
              </div>
              <div
                className="end-conversation-type-grid"
                role="radiogroup"
                aria-label="Conversation type"
              >
                {CONVERSATION_TYPE_OPTIONS.map(opt => {
                  const selected = leadType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!editable}
                      className={`end-conversation-type-option${
                        selected ? ' selected' : ''
                      }${!editable ? ' disabled' : ''}`}
                      onClick={() => selectLeadType(opt.value)}
                    >
                      <span>{opt.label}</span>
                      <span
                        className="end-conversation-type-radio"
                        aria-hidden="true"
                      >
                        {selected ? '✓' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </dl>
  );
}
